import api from "./api";
import { INDICATIONS_TYPES } from "./constants";
import { isValidMongoId } from "@/utils/validators";

const indicationService = {};
const endpoint = "/indications";

indicationService.createIndication = function (indication) {
  return api.post(endpoint, indication).then(({ data }) => data);
};

indicationService.get = function (patientId, indicationsFilter, options = {}) {
  const filters = getIndicationTypes(indicationsFilter);
  return api
    .get(endpoint, {
      params: { patientId, indicationTypeFilter: filters },
      ...options,
    })
    .then((res) => res.data);
};

indicationService.getById = function (indicationId) {
  return api.get(`${endpoint}/${indicationId}`).then(({ data }) => data);
};

indicationService.update = function (indicationId, payload) {
  return api
    .put(`${endpoint}/${indicationId}/edit`, payload)
    .then(({ data }) => data);
};

// Método reutilizable para guardar indicaciones - versión completa
indicationService.saveIndications = async function (
  savedIndications,
  patientId,
  appointmentId,
  typeFilter = null,
  store = null,
  options = {}
) {
  const {
    isSavingIndications = false,
    onTriggerUpload = null,
    onUpdateIndication = null,
    onSetSavingStatus = null,
  } = options;

  if (isSavingIndications) {
    return [];
  }

  let pendingIndications;
  if (typeFilter && typeFilter.length > 1) {
    pendingIndications = savedIndications.filter(
      (indication) =>
        !indication.createdAt &&
        (typeFilter ? typeFilter.includes(indication.type) : true)
    );
  } else {
    pendingIndications = savedIndications.filter(
      (indication) => !indication.createdAt
    );
  }

  if (pendingIndications.length === 0) {
    return [];
  }

  const hasFiles = pendingIndications.some((indication) => indication.hasFiles);

  try {
    if (onSetSavingStatus) {
      onSetSavingStatus(true);
    }

    const createdIndications = [];

    for (const indication of pendingIndications) {
      const values = {};

      for (const key in indication.defaultValues) {
        const value = indication.defaultValues[key].value;
        const isRequired = indication.defaultValues[key].required;

        if (value || isRequired) {
          if (key === "isLongTerm") {
            values[key] = value === "Sí" ? true : false;
          } else if (key === "generic") {
            values[key] = value === "Sí" ? true : false;
          } else if (key === "replaceable") {
            values[key] = value === "Sí" ? "S" : "N";
          } else if (key === "medication") {
            if (value) {
              const medication = store?.state?.cacheMedications?.[value];
              if (isValidMongoId(value)) {
                values.medication = value;
              }
              if (medication) {
                values.publicMedication = {
                  name: medication.name,
                  drugId: medication._id,
                };
              }
            }
          } else if (key === "diagnosis") {
            if (value) {
              const diagnosis = store?.state?.cacheDiagnoses?.[value];
              if (isValidMongoId(value)) {
                values.diagnosis = {
                  id: value,
                  name: diagnosis?.description || "",
                };
              } else if (typeof value === "object" && value.id) {
                values.diagnosis = {
                  id: value.id,
                  name: value.name || value.description || "",
                };
              }
            }
          } else if (key === "patientEmail") {
            if (value && value.trim() !== "") {
              values.patientEmail = value.trim();
            }
          } else {
            values[key] = value;
          }
        }
      }
      const parsedIndication = {
        ...values,
        type: indication.type,
        patientId: patientId,
        appointment: appointmentId,
      };

      const createdIndication = await this.createIndication(parsedIndication);

      const updatedIndication = {
        ...indication,
        _id: createdIndication._id,
        createdAt: createdIndication.createdAt,
      };

      if (indication.hasFiles && onTriggerUpload) {
        onTriggerUpload(updatedIndication);
      }

      if (onUpdateIndication) {
        onUpdateIndication(updatedIndication);
      }

      createdIndications.push(updatedIndication);
    }

    return createdIndications;
  } catch (error) {
    console.log(error);
    throw error;
  } finally {
    if (!hasFiles && onSetSavingStatus) {
      onSetSavingStatus(false);
    }
  }
};

const getIndicationTypes = (indicationsFilter) => {
  // TODO: Remove this. By default, if the don't scpecify a filter, we should return everything without providing every single type
  if (!indicationsFilter) return INDICATIONS_TYPES.map((item) => item.type);

  const indicationTypes = indicationsFilter.map(
    (indication) => indication.type
  );
  return indicationTypes;
};

export default indicationService;
